import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Skeleton
} from '@mui/material'
import {
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AccountBalance as FundsIcon,
  TrendingUp as PositionsIcon,
  Person as AccountsIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { useAuth } from '../../../../auth/useAuth'
import { useTableSort } from '../../../../hooks/useTableSort'
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader'

interface DeletedItem {
  id: number
  table: 'funds' | 'positions' | 'accounts'
  name: string
  deletedDate: Date | string
  deletedBy: string
  originalData: any
}

const DeletedItemsManagement: React.FC = () => {
  const { user, getAccessToken } = useAuth()
  const [deletedItems, setDeletedItems] = useState<DeletedItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Filter state
  const [selectedTable, setSelectedTable] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  
  // Table state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  
  // Dialog state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<DeletedItem | null>(null)

  // Load deleted items with proper error handling and race condition prevention
  const loadDeletedItems = async (signal?: AbortSignal) => {
    console.log('LoadDeletedItems called')
    setLoading(true)
    setError(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const params = new URLSearchParams()
      if (selectedTable) params.append('table', selectedTable)
      if (dateFrom) params.append('dateFrom', dateFrom.toISOString())
      if (dateTo) params.append('dateTo', dateTo.toISOString())
      
      console.log('Making API request to deleted-items')
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/deleted-items/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal
      })
      
      // Check if request was aborted
      if (signal?.aborted) {
        console.log('Request was aborted')
        return
      }
      
      if (!response.ok) {
        throw new Error(`Failed to load deleted items: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()
      console.log('API response data:', data)
      
      // Only update state if request wasn't aborted
      if (!signal?.aborted) {
        // Ensure data is an array
        const itemsArray = Array.isArray(data) ? data : []
        console.log('Setting deleted items:', itemsArray)
        setDeletedItems(itemsArray)
      }
    } catch (err) {
      // Don't update state if request was aborted
      if (signal?.aborted) {
        console.log('Request aborted in catch block')
        return
      }
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to load deleted items'
      console.error('Error loading deleted items:', errorMessage)
      setError(errorMessage)
      
      // For development, show sample data only if it's not an abort error
      if (!errorMessage.includes('aborted')) {
        console.log('Setting sample data due to error')
        setDeletedItems([
          {
            id: 1,
            table: 'funds',
            name: 'Deleted Fund Alpha',
            deletedDate: new Date('2024-01-15'),
            deletedBy: user?.username || 'admin',
            originalData: {
              Fund: 'Deleted Fund Alpha',
              Issuer: 'Alpha Capital',
              Strategy: 'Private Equity',
              Fund_Size: 100000000
            }
          },
          {
            id: 2,
            table: 'positions',
            name: 'Tech Company B Position',
            deletedDate: new Date('2024-02-20'),
            deletedBy: user?.username || 'admin',
            originalData: {
              Position_Name: 'Tech Company B Position',
              Position_Fund: 'Growth Fund II',
              Position_Industry: 'Technology',
              Position_Amount_Invested: 25000000
            }
          },
          {
            id: 3,
            table: 'accounts',
            name: 'Closed Pension Account',
            deletedDate: new Date('2024-03-10'),
            deletedBy: user?.username || 'admin',
            originalData: {
              AccountName: 'Closed Pension Account',
              InvestorName: 'Pension Fund XYZ',
              AccountType: 'Institutional',
              CommitmentAmount: 50000000
            }
          }
        ])
      }
    } finally {
      // Only update loading state if request wasn't aborted
      if (!signal?.aborted) {
        console.log('Setting loading to false')
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    const abortController = new AbortController()
    
    // Add a small delay to prevent rapid successive calls
    const timeoutId = setTimeout(() => {
      loadDeletedItems(abortController.signal)
    }, 300)
    
    return () => {
      clearTimeout(timeoutId)
      abortController.abort()
    }
  }, [selectedTable, dateFrom, dateTo])

  // Reset page when search term changes
  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  // Handle restore
  const handleRestore = async () => {
    if (!selectedItem) return
    
    setLoading(true)
    setError(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/deleted-items/${selectedItem.table}/${selectedItem.id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          restoredBy: user?.username || 'system'
        })
      })
      
      if (!response.ok) {
        throw new Error('Failed to restore item')
      }
      
      setSuccess(`${selectedItem.name} restored successfully`)
      setRestoreDialogOpen(false)
      setSelectedItem(null)
      loadDeletedItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Restore failed')
    } finally {
      setLoading(false)
    }
  }

  // Handle permanent delete
  const handlePermanentDelete = async () => {
    if (!selectedItem) return
    
    setLoading(true)
    setError(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/deleted-items/${selectedItem.table}/${selectedItem.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to permanently delete item')
      }
      
      setSuccess(`${selectedItem.name} permanently deleted`)
      setPermanentDeleteDialogOpen(false)
      setSelectedItem(null)
      loadDeletedItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Permanent delete failed')
    } finally {
      setLoading(false)
    }
  }

  // Filter deleted items
  const filteredItems = deletedItems.filter(item => {
    const matchesSearch = !searchTerm || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.deletedBy.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesSearch
  })

  // Apply sorting to filtered items
  const { sortedData: sortedItems, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredItems,
    initialSort: [{ field: 'deletedDate', direction: 'desc' }]
  })

  // Paginated items
  const paginatedItems = sortedItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  // Get table icon
  const getTableIcon = (table: string) => {
    switch (table) {
      case 'funds': return <FundsIcon />
      case 'positions': return <PositionsIcon />
      case 'accounts': return <AccountsIcon />
      default: return <InfoIcon />
    }
  }

  // Get table color
  const getTableColor = (table: string) => {
    switch (table) {
      case 'funds': return 'primary'
      case 'positions': return 'success'
      case 'accounts': return 'info'
      default: return 'default'
    }
  }

  // Calculate summary stats
  const summaryStats = {
    total: deletedItems.length,
    funds: deletedItems.filter(item => item.table === 'funds').length,
    positions: deletedItems.filter(item => item.table === 'positions').length,
    accounts: deletedItems.filter(item => item.table === 'accounts').length
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
          Deleted Items Management
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Warning Notice */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> This section shows all soft-deleted records. 
            Items can be restored or permanently deleted. Permanent deletion cannot be undone.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" color="error">
                          {summaryStats.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Deleted
                        </Typography>
                      </Box>
                      <WarningIcon color="error" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" color="primary">
                          {summaryStats.funds}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Deleted Funds
                        </Typography>
                      </Box>
                      <FundsIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" color="success.main">
                          {summaryStats.positions}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Deleted Positions
                        </Typography>
                      </Box>
                      <PositionsIcon color="success" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" color="info.main">
                          {summaryStats.accounts}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Deleted Accounts
                        </Typography>
                      </Box>
                      <AccountsIcon color="info" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Filters */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">
                  Filters
                </Typography>
              </Box>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    placeholder="Search deleted items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Table Type</InputLabel>
                    <Select
                      value={selectedTable}
                      onChange={(e) => setSelectedTable(e.target.value)}
                      label="Table Type"
                    >
                      <MenuItem value="">All Tables</MenuItem>
                      <MenuItem value="funds">Funds</MenuItem>
                      <MenuItem value="positions">Positions</MenuItem>
                      <MenuItem value="accounts">Accounts</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label="Deleted From"
                    value={dateFrom}
                    onChange={(date) => setDateFrom(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label="Deleted To"
                    value={dateTo}
                    onChange={(date) => setDateTo(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={() => loadDeletedItems()}
                    disabled={loading}
                    fullWidth
                    size="small"
                    sx={{ height: '40px' }}
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Deleted Items Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">
                  Deleted Items ({filteredItems.length})
                </Typography>
                <IconButton onClick={() => loadDeletedItems()} disabled={loading}>
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableHeader
                        field="table"
                        label="Type"
                        sortDirection={getSortDirection('table')}
                        sortIndex={getSortIndex('table')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="name"
                        label="Name"
                        sortDirection={getSortDirection('name')}
                        sortIndex={getSortIndex('name')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="deletedDate"
                        label="Deleted Date"
                        sortDirection={getSortDirection('deletedDate')}
                        sortIndex={getSortIndex('deletedDate')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="deletedBy"
                        label="Deleted By"
                        sortDirection={getSortDirection('deletedBy')}
                        sortIndex={getSortIndex('deletedBy')}
                        onSort={handleSort}
                      />
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      // Show skeleton rows while loading
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="rectangular" width={80} height={24} /></TableCell>
                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Skeleton variant="circular" width={24} height={24} />
                              <Skeleton variant="circular" width={24} height={24} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      paginatedItems.map((item) => (
                        <TableRow key={`${item.table}-${item.id}`}>
                          <TableCell>
                            <Chip
                              icon={getTableIcon(item.table)}
                              label={item.table.toUpperCase()}
                              color={getTableColor(item.table) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            {typeof item.deletedDate === 'string' 
                              ? new Date(item.deletedDate).toLocaleDateString('en-GB')
                              : item.deletedDate.toLocaleDateString('en-GB')
                            }
                          </TableCell>
                          <TableCell>{item.deletedBy}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Restore">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedItem(item)
                                    setRestoreDialogOpen(true)
                                  }}
                                  disabled={loading}
                                  color="success"
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permanently Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedItem(item)
                                    setPermanentDeleteDialogOpen(true)
                                  }}
                                  disabled={loading}
                                  color="error"
                                >
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredItems.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10))
                  setPage(0)
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Restore Confirmation Dialog */}
        <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
          <DialogTitle>Confirm Restore</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to restore "{selectedItem?.name}"?
              This will make the item active again and available in the main system.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRestore} color="success" disabled={loading}>
              Restore
            </Button>
          </DialogActions>
        </Dialog>

        {/* Permanent Delete Confirmation Dialog */}
        <Dialog open={permanentDeleteDialogOpen} onClose={() => setPermanentDeleteDialogOpen(false)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="error" />
              Confirm Permanent Delete
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action cannot be undone!
              </Typography>
            </Alert>
            <Typography>
              Are you sure you want to permanently delete "{selectedItem?.name}"?
              This will completely remove the item from the database and cannot be recovered.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPermanentDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePermanentDelete} color="error" disabled={loading}>
              Permanently Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default DeletedItemsManagement
